/**
 * Panel de inicio del backoffice (ADM-07).
 *
 * Responde en una pantalla las preguntas del dueno: cuanto se vendio —en el
 * sitio y por fuera—, cuanto entro por WhatsApp, que le falta al catalogo y que
 * pedidos pagados siguen esperando su boleta o factura. Lo ultimo es trabajo
 * pendiente con alguien que ya pago, asi que sale siempre, aunque valga cero.
 */
import { Link } from 'react-router-dom';

import { BarraAdmin } from '../../componentes/administracion/BarraAdmin';
import { GraficoBarras } from '../../componentes/administracion/GraficoBarras';
import { ListaBarras } from '../../componentes/administracion/ListaBarras';
import { PanelAdmin } from '../../componentes/administracion/PanelAdmin';
import { TarjetaKpi } from '../../componentes/administracion/TarjetaKpi';
import { AvisoError, Cargando } from '../../componentes/comunes/EstadoPeticion';
import { Icono } from '../../componentes/comunes/Icono';
import { usePeticion } from '../../hooks/use-peticion';
import { Seo } from '../../seo/Seo';
import { obtenerPanel } from '../../servicios/administracion.servicio';
import { numero, pesos } from '../../utilidades/formato';
import { rutas } from '../../utilidades/rutas';

export function Panel() {
  const { estado, datos, error } = usePeticion((senal) => obtenerPanel(senal), []);

  return (
    <>
      <Seo
        titulo="Panel"
        descripcion="Resumen de la operacion de la tienda."
        ruta={rutas.administracion.panel()}
        sinIndexar
      />

      <BarraAdmin titulo="Panel" bajada="Resumen de la operacion" />

      <div className="admin-cuerpo">
        {estado === 'cargando' ? <Cargando que="el panel" /> : null}
        {estado === 'error' ? <AvisoError error={error} /> : null}

        {datos !== null ? (
          <>
            <ul className="tarjetas-kpi" aria-label="Cifras del periodo">
              <TarjetaKpi
                icono="grafico"
                etiqueta="Vendido en el sitio"
                valor={pesos(datos.metricas.montoVendido)}
                detalle={`${numero(datos.metricas.ventasPagadas)} pedidos pagados`}
              />
              <TarjetaKpi
                icono="tienda"
                etiqueta="Vendido por fuera"
                valor={pesos(datos.metricas.montoVentasExternas)}
                detalle={`${numero(datos.metricas.ventasExternas)} ventas anotadas en el inventario`}
              />
              <TarjetaKpi
                tono="wa"
                icono="wa"
                etiqueta="Contactos por WhatsApp"
                valor={numero(datos.metricas.contactosWhatsapp)}
                detalle="Conversaciones abiertas desde el sitio"
              />
              <TarjetaKpi
                tono={datos.metricas.pedidosSinDocumento > 0 ? 'alerta' : 'verde'}
                icono="factura"
                etiqueta="Pedidos sin boleta o factura"
                valor={numero(datos.metricas.pedidosSinDocumento)}
                detalle={
                  datos.metricas.pedidosSinDocumento > 0
                    ? 'Ya pagaron: falta emitir y subir el documento'
                    : 'Todo al dia'
                }
              />
            </ul>

            <div className="panel-doble">
              <div>
                <PanelAdmin
                  titulo="Venta diaria del sitio"
                  bajada={`Ultimos ${datos.ventasDiarias.length} dias`}
                  extra={
                    <Link className="btn btn-xs btn-contorno" to={rutas.administracion.pedidos()}>
                      Ver pedidos
                      <Icono nombre="flecha" />
                    </Link>
                  }
                >
                  <GraficoBarras
                    valores={datos.ventasDiarias}
                    etiqueta="Venta diaria de los ultimos dias"
                  />
                </PanelAdmin>

                <PanelAdmin
                  titulo="Estado del catalogo"
                  extra={
                    <Link className="btn btn-xs btn-contorno" to={rutas.administracion.inventario()}>
                      Ver inventario
                      <Icono nombre="flecha" />
                    </Link>
                  }
                >
                  <ul className="lista-cifras">
                    <li>
                      <span>Productos publicados</span>
                      <span className="etq etq-neutra">
                        {numero(datos.estadoCatalogo.publicados)}
                      </span>
                    </li>
                    <li>
                      <span>Ocultos</span>
                      <span className="etq etq-neutra">{numero(datos.estadoCatalogo.ocultos)}</span>
                    </li>
                    {/*
                      Publicados en el panel pero con precio en cero: la tienda
                      no los muestra. Solo aparece si hay alguno, porque en cero
                      no le dice nada a nadie.
                    */}
                    {datos.estadoCatalogo.publicadosSinPrecio > 0 ? (
                      <li>
                        <span>Publicados sin precio (no se ven)</span>
                        <span className="etq etq-alerta">
                          {numero(datos.estadoCatalogo.publicadosSinPrecio)}
                        </span>
                      </li>
                    ) : null}
                    <li>
                      <span>Sin stock</span>
                      <span
                        className={
                          datos.estadoCatalogo.sinStock > 0 ? 'etq etq-critico' : 'etq etq-neutra'
                        }
                      >
                        {numero(datos.estadoCatalogo.sinStock)}
                      </span>
                    </li>
                    <li>
                      <span>Stock critico</span>
                      <span
                        className={
                          datos.estadoCatalogo.stockCritico > 0
                            ? 'etq etq-alerta'
                            : 'etq etq-neutra'
                        }
                      >
                        {numero(datos.estadoCatalogo.stockCritico)}
                      </span>
                    </li>
                    <li>
                      <span>Busquedas que no encontraron nada</span>
                      <Link className="etq etq-neutra" to={rutas.administracion.busquedas()}>
                        {numero(datos.metricas.busquedasSinResultado)}
                      </Link>
                    </li>
                  </ul>
                </PanelAdmin>
              </div>

              <div>
                {/*
                  El desglose es lo que convierte el total en algo accionable: no
                  es lo mismo que las consultas salgan de las fichas —donde el
                  comprador ya sabe que quiere— que del boton flotante, que suele
                  ser alguien que no encontro lo que buscaba. Va en barras y no
                  en una lista de numeros: son nueve origenes y lo que importa es
                  cual concentra la mayoria, no leer cada cifra suelta.
                */}
                <PanelAdmin
                  titulo="De donde salen los contactos"
                  bajada="Cada clic a un boton de WhatsApp, por parte del sitio"
                >
                  {datos.contactosWhatsapp.length > 0 ? (
                    <ListaBarras
                      items={datos.contactosWhatsapp.map((contacto) => ({
                        etiqueta: contacto.nombre,
                        valor: contacto.cantidad,
                      }))}
                    />
                  ) : (
                    <p className="panel-vacio">Todavia nadie escribio por WhatsApp.</p>
                  )}
                </PanelAdmin>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </>
  );
}
